import Compressor from 'compressorjs';
import { err, ok, Result } from "neverthrow";

/**
 * Compresses an image represented as a Base64 string using compressorjs.
 *
 * Resizes to fit within maxWidth/maxHeight (aspect ratio preserved) and
 * compresses to JPEG at the given quality.
 *
 * @param base64String - The Base64 string of the image to be compressed.
 * @param maxWidth - The maximum width of the compressed image.
 * @param maxHeight - The maximum height of the compressed image.
 * @param quality - Quality from 0 to 1 (1 = best quality).
 *
 * @returns A Promise that resolves to a `Result` containing either:
 * - `ok` with the compressed image as a Base64 data URL, or
 * - `err` with an error if compression fails.
 */
export function compressImageBase64(
  base64String: string,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<Result<string, any>> {
  return new Promise((resolve) => {
    try {
      const blob = base64ToBlob(base64String);

      new Compressor(blob, {
        quality,
        maxWidth,
        maxHeight,
        mimeType: 'image/jpeg',
        convertTypes: ['image/png', 'image/webp', 'image/jpeg'],
        success: async (result) => {
          try {
            const compressedBase64 = await blobToBase64(result);

            const originalSize = base64String.length;
            const compressedSize = compressedBase64.length;
            const compressionPercentage =
              ((originalSize - compressedSize) / originalSize) * 100;

            console.log(
              `Compression achieved: ${compressionPercentage.toFixed(2)}%`
            );

            resolve(ok(compressedBase64));
          } catch (error) {
            resolve(err(error));
          }
        },
        error: (error) => {
          resolve(err(error));
        },
      });
    } catch (error) {
      resolve(err(error));
    }
  });
}

function base64ToBlob(base64String: string): Blob {
  const [header, data] = base64String.includes(',')
    ? base64String.split(',')
    : ['data:image/jpeg;base64', base64String];

  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch?.[1] || 'image/jpeg';
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
